import type {
    ForkliftModel,
    OptionChoice,
    PaintChoice,
    PowerChoice,
    BatteryKey,
    TireKey
} from '@/types'

/** Shown only when the lineup cannot be read from the database. */
export const fallbackModels: ForkliftModel[] = [
    {
        id: null,
        slug: 'fq-18',
        name: "FQ-18",
        edition: "Compact",
        tagline: "Nimble in narrow aisles",
        description: "A tight 1.8 m turning radius and a 48 V lithium pack make the FQ-18 the easy choice for retail backrooms, mezzanines and busy cross-docks.",
        badge: null,
        capacityKg: 1800,
        liftHeightM: 4.8,
        batteryVoltage: 48,
        runtimeHours: 8,
        topSpeedKmh: 14,
        turningRadiusM: 1.8,
        priceUsd: 28900
    },
    {
        id: null,
        slug: 'fq-30',
        name: "FQ-30",
        edition: "Pro",
        tagline: "The all-round workhorse",
        description: "Our best seller. Two full shifts on the extended pack, a triplex mast to 6.0 m and the smoothest hydraulics in its class.",
        badge: "Most popular",
        capacityKg: 3000,
        liftHeightM: 6,
        batteryVoltage: 80,
        runtimeHours: 10,
        topSpeedKmh: 17,
        turningRadiusM: 2.2,
        priceUsd: 41500
    },
    {
        id: null,
        slug: 'fq-50',
        name: "FQ-50",
        edition: "Heavy",
        tagline: "Built for the heavy stuff",
        description: "5 tonnes to 7.5 m with a reinforced mast and an IP65 drivetrain, ready for timber, steel and block yards in any weather.",
        badge: null,
        capacityKg: 5000,
        liftHeightM: 7.5,
        batteryVoltage: 96,
        runtimeHours: 9,
        topSpeedKmh: 19,
        turningRadiusM: 2.7,
        priceUsd: 64900
    }
]

export const paints: PaintChoice[] = [
    { key: 'yellow', name: "FORQ Yellow", hex: '#FFC21A', priceUsd: 0 },
    { key: 'graphite', name: "Graphite", hex: '#3A3F47', priceUsd: 0 },
    { key: 'orange', name: "Signal Orange", hex: '#FF6B1A', priceUsd: 390 },
    { key: 'red', name: "Fleet Red", hex: '#D62839', priceUsd: 390 },
    { key: 'blue', name: "Electric Blue", hex: '#2F6BFF', priceUsd: 390 },
    { key: 'white', name: "Arctic White", hex: '#E9ECEF', priceUsd: 390 }
]

export const batteries: PowerChoice<BatteryKey>[] = [
    { key: 'standard', name: "Standard pack", detail: "Covers a full shift with breaks to spare.", priceUsd: 0 },
    { key: 'extended', name: "Extended pack", detail: "60% more runtime for double shifts.", priceUsd: 4200 }
]

export const tires: PowerChoice<TireKey>[] = [
    { key: 'cushion', name: "Cushion tires", detail: "Smooth concrete floors indoors.", priceUsd: 0 },
    { key: 'pneumatic', name: "Pneumatic tires", detail: "Grip for yards, ramps and gravel.", priceUsd: 1100 }
]

export const options: OptionChoice[] = [
    { key: 'cab', name: "Enclosed all-weather cab", detail: "Glazed doors, heater and wipers for outdoor work.", priceUsd: 3900 },
    { key: 'safety', name: "Blue spot and red zone lights", detail: "Projects a warning ahead and marks the no-go zone.", priceUsd: 690 },
    { key: 'camera', name: "360° camera and pedestrian radar", detail: "Slows the truck when someone steps too close.", priceUsd: 2950 },
    { key: 'sideshift', name: "Integrated side shifter", detail: "Line up pallets without repositioning the truck.", priceUsd: 1850 },
    { key: 'positioner', name: "Hydraulic fork positioner", detail: "Adjust fork spread from the seat for mixed loads.", priceUsd: 2400 }
]

/** Extended pack runtime multiplier, shown in the configurator specs. */
export const EXTENDED_RUNTIME_FACTOR = 1.6

/** Monthly lease estimate as a share of the build price, roughly 60 months. */
export const LEASE_FACTOR = 0.0215
